import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./CartItems.css";

// Static mock cart data (replace with real API later)
const mockCartItems = [
  {
    id: 1,
    productId: 101,
    productName: "Wireless Earbuds Pro",
    price: 49.99,
    quantity: 2,
    imageUrl:
      "https://images.unsplash.com/photo-1600294037681-c80b4cb5b434?w=100",
    description:
      "High-quality noise-cancelling earbuds with 30-hour battery life and fast charging.",
    addedAt: new Date(Date.now() - 1000 * 60 * 45).toISOString(), // 45 min ago
  },
  {
    id: 2,
    productId: 102,
    productName: "Smart Watch Series 8",
    price: 299.0,
    quantity: 1,
    imageUrl: "https://images.unsplash.com/photo-1546868871-7041f2a55e12?w=100",
    description:
      "Fitness tracking, heart rate monitor, blood oxygen, GPS, always-on display, water resistant.",
    addedAt: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(), // 3 hours ago
  },
  {
    id: 3,
    productId: 103,
    productName: "Portable Power Bank 20000mAh",
    price: 34.99,
    quantity: 3,
    imageUrl:
      "https://images.unsplash.com/photo-1618410320929-135a5d8d5e1e?w=100",
    description:
      "Fast charging power bank with dual USB ports and LED display.",
    addedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(), // 2 days ago
  },
  {
    id: 4,
    productId: 104,
    productName: "4K Action Camera",
    price: 89.99,
    quantity: 1,
    imageUrl:
      "https://images.unsplash.com/photo-1617791160505-6f00504e3519?w=100",
    description:
      "Waterproof 4K camera with wide-angle lens, image stabilization and WiFi connectivity.",
    addedAt: new Date(Date.now() - 1000 * 60 * 10).toISOString(), // 10 min ago
  },
];

const CartItems = ({ open, onClose }) => {
  const navigate = useNavigate();

  // Local state for cart items (simulating redux)
  const [cartItems, setCartItems] = useState(mockCartItems);
  const [expanded, setExpanded] = useState([]);

  // Sync expanded state when cart length changes
  useEffect(() => {
    setExpanded((prev) => {
      const currentLength = cartItems.length;
      let newExpanded = [...prev];
      while (newExpanded.length < currentLength) newExpanded.push(false);
      if (newExpanded.length > currentLength)
        newExpanded = newExpanded.slice(0, currentLength);
      return newExpanded;
    });
  }, [cartItems.length]);

  const handleNavigation = useCallback(
    (productId) => {
      navigate(`/productView?productid=${productId}`);
      onClose();
    },
    [navigate, onClose]
  );

  const handleIncrement = useCallback((e, itemId) => {
    e.stopPropagation();
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, quantity: item.quantity + 1 } : item
      )
    );
  }, []);

  const handleDecrement = useCallback((e, itemId) => {
    e.stopPropagation();
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === itemId && item.quantity > 1
          ? { ...item, quantity: item.quantity - 1 }
          : item
      )
    );
  }, []);

  const handleRemove = useCallback((e, itemId) => {
    e.stopPropagation();
    setCartItems((prev) => prev.filter((item) => item.id !== itemId));
  }, []);

  // Format relative time
  const getRelativeTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    if (diffInMinutes < 1) return "Just added";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const mappedCartItems = cartItems.map((item) => ({
    ...item,
    time: getRelativeTime(item.addedAt),
  }));

  const toggleExpanded = useCallback((index) => {
    setExpanded((prev) => {
      const newExpanded = [...prev];
      newExpanded[index] = !newExpanded[index];
      return newExpanded;
    });
  }, []);

  // Skeleton loader
  const SkeletonCartItem = () => (
    <div className="cart-item skeleton">
      <div className="product-image skeleton-circle"></div>
      <div className="cart-content">
        <div className="text-with-toggle">
          <div className="product-name skeleton-line"></div>
          <div className="toggle-btn skeleton-line small"></div>
        </div>
        <div className="cart-time skeleton-line small"></div>
      </div>
      <div className="quantity-control skeleton-image"></div>
    </div>
  );

  if (!open) return null;

  const isLoading = false; // You can toggle this for testing skeleton

  return (
    <>
      <div className="overlay" onClick={onClose}></div>
      <div className="cart-sheet">
        <div className="cart-header">
          <h3>Cart Items</h3>
          <span className="close-btn" onClick={onClose}>
            ✕
          </span>
        </div>

        <div className="cart-list">
          {isLoading && cartItems.length === 0 ? (
            <>
              {[...Array(5)].map((_, i) => (
                <SkeletonCartItem key={i} />
              ))}
            </>
          ) : mappedCartItems.length === 0 ? (
            <div className="no-items-text">Your cart is empty</div>
          ) : (
            mappedCartItems.map((item, index) => {
              const fullText = item.description;
              const isLongText = fullText.length > 40;
              const isExpanded = expanded[index];
              const displayDescription =
                isLongText && !isExpanded
                  ? fullText.substring(0, 40) + "..."
                  : fullText;

              return (
                <div
                  key={item.id}
                  className="cart-item"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleNavigation(item.productId)}
                >
                  <img
                    src={item.imageUrl}
                    alt={item.productName}
                    className="product-image"
                  />

                  <div className="cart-content">
                    <div className="text-with-toggle">
                      <span className="product-name">
                        <strong>{item.productName}</strong>{" "}
                        {displayDescription && ` - ${displayDescription}`}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                      <span className="product-price">
                        ${item.price.toFixed(2)} × {item.quantity}
                      </span>

                      <div className="quantity-control">
                        <button
                          onClick={(e) => handleDecrement(e, item.id)}
                          disabled={item.quantity <= 1}
                        >
                          -
                        </button>
                        <span>{item.quantity}</span>
                        <button onClick={(e) => handleIncrement(e, item.id)}>
                          +
                        </button>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between">
                      <span className="cart-time">{item.time}</span>
                      <div className="d-flex gap-2">
                        <span
                          className="remove-btn"
                          onClick={(e) => handleRemove(e, item.id)}
                        >
                          Remove Item
                        </span>
                        {isLongText && (
                          <span
                            className="toggle-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpanded(index);
                            }}
                          >
                            {isExpanded ? "Less" : "More"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {mappedCartItems.length > 0 && (
          <div className="cart-footer">
            <div className="total">
              Total: $
              {mappedCartItems
                .reduce((sum, item) => sum + item.price * item.quantity, 0)
                .toFixed(2)}
            </div>
            <button className="checkout-btn-cart">Proceed to Checkout</button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartItems;
